import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import * as turf from "@turf/turf";
import _ from "lodash";
import { DateTime } from "luxon";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { LayerProps, MapRef, SourceProps } from "react-map-gl/mapbox";
import Map, { GeolocateControl, Layer, NavigationControl, Popup, Source, useMap } from "react-map-gl/mapbox";
import * as factorApi from "../../../../apis/factorApi";
import * as flightsPlotsApi from "../../../../apis/flightsPlotsApi.js";
import { useAuth0 } from "../../../../auth/auth0";
import { useModuleNavigation } from "../../../../hooks/useModuleNavigation";
import { useUserAuth } from "../../../../hooks/useUserAuth";
import { DeckGLOverlay, DrawControl } from "./MapControls";
import { MeasureModes } from "./MapControls/DrawControl";
import * as styles from "./MapStyles";
import type { PlotLLProps, PlotProps } from "./StateManagement";
import {
	useData,
	useDataDispatch,
	useDrawDispatch,
	useGraphDispatch,
	useViewer,
	useViewerDispatch,
} from "./StateManagement";

import type { Feature, FeatureCollection, MultiPolygon, Point, Polygon } from "geojson";
import { FilterSpecification } from "mapbox-gl";
import { fetchFromAzureFileShare } from "../../../../apis/dataLayerApi";
import BoxBlockLine from "../../../../assets/BoxBlockLine.svg";
import BoxFlagFilled from "../../../../assets/BoxFlagFilled.svg";
import BoxFlagLine from "../../../../assets/BoxFlagLine.svg";
import BoxWarningFilled from "../../../../assets/BoxWarningFilled.svg";
import BoxWarningLine from "../../../../assets/BoxWarningLine.svg";
import HashPatternInverted from "../../../../assets/hash-pattern-inverted.svg";
import HashPattern from "../../../../assets/hash-pattern.svg";
import { getContrastColor } from "../../../../utilities/jsUtils.js";

interface MarkedPlot {
	plotId: string;
	flightId?: string;
	plotStatusName: "Excluded" | "Flagged";
	plotStatusId: string;
	excludeFromAssessment: boolean;
	excludeFromSubsequentAssessments: boolean;
	dateAndTime: string;
	isAllData: boolean;
	notes: string;
	annotationClassifier: string;
	oneTime: boolean;
	modifiedBy: string;
	modifiedDateTime: string;
}

interface MarkedQuantifiedRegion {
	quantifiedRegionId: string;
	plotId: string;
	flightId?: string;
	quantifiedRegionStatusName: "Excluded" | "Flagged" | "PartiallyExcluded" | "PartiallyFlagged";
	excludeFromAssessment: boolean;
	excludeFromSubsequentAssessments: boolean;
	dateAndTime: string;
	quantifiedRegionStatusId: string;
	isAllData: boolean;
	notes: string;
	annotationClassifier: string;
	oneTime: boolean;
	modifiedBy: string;
	modifiedDateTime: string;
}

interface PlotStatus {
	status: string;
	statusIcon: string;
	level: string;
	notes: string;
	quantifiedRegionNames?: { [key: string]: string[] };
	statusOrder: number;
	levelOrder: number;
	isAllData: boolean;
	annotationClassifier: string;
	modifiedBy: string;
	modifiedDateTime: string;
}

interface PlotStatusMap {
	[plotId: string]: {
		[date: number]: PlotStatus[];
	};
}

interface QuantifiedRegionOutline {
	count: number;
	quantifiedRegionTypeId: string;
}

interface PlotData {
	plots: PlotProps[];
	markedPlots: MarkedPlot[];
	markedQuantifiedRegions: MarkedQuantifiedRegion[];
	quantifiedRegionOutlines: QuantifiedRegionOutline[];
}

const MFAMapWrapper = () => {
	const ICON_SIZE = 64;
	const INIT_VIEW_STATE = {
		latitude: 39.8097343,
		longitude: -98.5556199,
		zoom: 3,
	};

	const mapboxTileSourceBaseId = "mapbox-tile-";
	const mapboxTileLayerBaseId = "mapbox-layer-";

	const iconSvgs = useMemo(
		() => ({
			"flag-filled": BoxFlagFilled,
			"flag-line": BoxFlagLine,
			"warn-filled": BoxWarningFilled,
			"warn-line": BoxWarningLine,
			"block-line": BoxBlockLine,
			"hash-pattern": HashPattern,
			"hash-pattern-inverted": HashPatternInverted,
		}),
		[],
	);

	const statusIconMap = useMemo(
		() => ({
			Excluded: "warn-filled",
			PartiallyExcluded: "warn-line",
			Flagged: "flag-filled",
			PartiallyFlagged: "flag-line",
		}),
		[],
	);

	const { getTokenSilently } = useAuth0();
	const userAuth = useUserAuth();
	const moduleNavigation = useModuleNavigation();

	const {
		viewerMapboxUploads,
		orthoVisible,
		heatmapVisible,
		dataLayerVisible,
		statusMarkersVisible,
		heatmapColorStops,
		deckLayers,
		selectedPlotIds,
		selectedOrthoFlight,
		heatmapOpacity,
		legendStatus,
		dataLayerGeometryUrl,
		plotAssociatedGeometryUrl,
		dataLayerGroupMap,
		plotsVisible,
		plotLabelsVisible,
		treatmentsVisible,
		experimentalDesign,
		treatmentNames,
		layerOrder,
		dataLayerColorMap,
		dataLayerOpacity,
	} = useViewer();

	const { updateSelectedPlotIds, updateSelectedGroup } = useViewerDispatch();
	const { heatmapData, hideExclusions, trialPolygon } = useData();
	const { updatePlotData, updateTrialPolygon, updateDataLayerNames, updateDataLayerValues } = useDataDispatch();
	const { drawRef, updateDrawnFeature } = useDrawDispatch();
	const { updateSelectedFeatures, updateAxis } = useGraphDispatch();
	const { MFAViewer } = useMap();

	const [mapLoaded, setMapLoaded] = useState(false);
	const [hoveredDataLayer, setHoveredDataLayer] = useState<number | null>(null);
	const [cursor, setCursor] = useState("auto");
	const [focusedAnnotation, setFocusedAnnotation] = useState<Feature<Point, PlotProps> | null>(null);
	const [popupVisible, setPopupVisible] = useState(false);
	const [popupCoords, setPopupCoords] = useState([0, 0]);
	const [popupOffsetScaleFactor, setPopupOffsetScaleFactor] = useState(1);
	const [popupOffset, setPopupOffset] = useState(32);

	// Camera snapshot taken when the user starts geolocation tracking, so the
	// geolocate control's fly-to can preserve their zoom/bearing/pitch (mapbox's
	// built-in fitBounds runs before onGeolocate and would otherwise reset them).
	const preGeolocateCamera = useRef<{ zoom: number; bearing: number; pitch: number } | null>(null);

	const {
		mutate: generateTreatmentPolygonsMutation,
		data: generatedTreatmentPolygonsResponse,
		isPending: isGeneratingTreatmentPolygons,
	} = useMutation({
		mutationFn: factorApi.generateTreatmentPolygons,
		// mutationKey: ["generateTreatmentPolygons", ...treatmentNames],
	});

	const getPlotsForFlight = async () => {
		if (selectedOrthoFlight) {
			const accessToken = await getTokenSilently();
			const [flightId] = selectedOrthoFlight.split("/");
			const json = flightsPlotsApi.getExistingFlightsPlotsInTrialAsync(
				flightId,
				moduleNavigation.trialId,
				userAuth.currentClientId,
				accessToken,
			);
			return json as Promise<PlotData>;
		}
		return null;
	};

	const { isLoading: plotsLoading, data: plotsData } = useQuery({
		queryKey: ["mfa viewer trial plots", userAuth.currentClientId, selectedOrthoFlight, moduleNavigation.trialId],
		queryFn: () => getPlotsForFlight(),
		enabled: !!selectedOrthoFlight && !!userAuth.currentClientId && !!moduleNavigation.trialId,
	});

	// useEffect(() => {
	// 	if (plotsData) {
	// 		// TODO: For testing; remove when done
	// 		updatePlotIds(_.map(plotsData.plots, "plotId"));
	// 	}
	// }, [plotsData]);

	const [{ data: dataLayerGeometry }, { data: plotAssociatedGeometry }] = useQueries({
		queries: [dataLayerGeometryUrl, plotAssociatedGeometryUrl].map((url) => ({
			enabled: !!url,
			queryKey: ["FetchFromAzureFileShare", url],
			// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
			queryFn: async () => {
				if (url) {
					const response = await fetchFromAzureFileShare(url);
					const result = (await response.json()) as FeatureCollection<
						Polygon | MultiPolygon,
						{ aerialfarmsStd: number; catValueNum: number | null; catValueText: string | null }
					> & {
						properties: { attributeDescriptions: string[] };
					};
					return result;
				}
			},
		})),
	});

	const dataLayerGeometryWithColors = useMemo(() => {
		if (dataLayerColorMap.length === 0 || !dataLayerGeometry) return undefined;
		const newFeatures = turf.featureReduce(
			dataLayerGeometry,
			(acc, curr) => {
				const newFeature: Feature<
					Polygon | MultiPolygon,
					{ aerialfarmsStd: number; color?: string; pattern?: string }
				> = turf.clone(curr);
				const layerId = curr.properties.aerialfarmsStd;
				let pattern: string = "";
				const color =
					curr.properties.aerialfarmsStd > 0 ? dataLayerColorMap[(layerId - 1) % dataLayerColorMap.length] : undefined;
				if (layerId / dataLayerColorMap.length > 1) {
					pattern = "hash-pattern";
					if (color && getContrastColor(color) === "#ffffff") {
						pattern += "-inverted";
					}
				}

				newFeature.properties = {
					...curr.properties,
					color: color,
					pattern: pattern,
				};
				acc.push(newFeature);
				return acc;
			},
			[] as Feature<Polygon | MultiPolygon, { aerialfarmsStd: number; color?: string }>[],
		);
		return turf.featureCollection(newFeatures);
	}, [dataLayerGeometry, dataLayerColorMap]);

	// -- PLOT FEATURES PIPELINE --
	// Step 1: Build plot status map
	const plotStatusMap: PlotStatusMap | null = useMemo(() => {
		if (!plotsData) return null;
		const learnedStatusMap: PlotStatusMap = {};

		const statusOrder = {
			Excluded: 0,
			Flagged: 1,
		};

		const groupedMarkedPlots = _.groupBy(plotsData.markedPlots, "plotId");
		const groupedMarkedQRs = _.groupBy(plotsData.markedQuantifiedRegions, "plotId");

		_.forOwn(groupedMarkedPlots, (mps, plotId) => {
			mps.forEach((pdef) => {
				const dateAndTime = (pdef.dateAndTime ? DateTime.fromISO(pdef.dateAndTime) : DateTime.utc(0)).toMillis();
				const plotStatus = {
					status: pdef.plotStatusName,
					statusIcon: statusIconMap[pdef.plotStatusName],
					level: "Entire Plot",
					notes: pdef.notes ?? "",
					quantifiedRegionNames: {},
					statusOrder: statusOrder[pdef.plotStatusName],
					levelOrder: 0,
					isAllData: pdef.isAllData,
					annotationClassifier: pdef.annotationClassifier,
					modifiedBy: pdef.modifiedBy,
					modifiedDateTime: pdef.modifiedDateTime,
				};

				if (learnedStatusMap[plotId]?.[dateAndTime]) {
					learnedStatusMap[plotId][dateAndTime].push(plotStatus);
				} else if (learnedStatusMap[plotId]) {
					learnedStatusMap[plotId][dateAndTime] = [plotStatus];
				} else {
					learnedStatusMap[plotId] = { [dateAndTime]: [plotStatus] };
				}
			});
		});

		_.forOwn(groupedMarkedQRs, (mqrs, plotId) => {
			const datedMarkedQRs = _.map(mqrs, (mqr) => {
				if (mqr.isAllData) {
					return {
						...mqr,
						dateAndTime: DateTime.utc(0).toISOTime(),
					};
				}
				return mqr;
			}) as MarkedQuantifiedRegion[];

			const markedTimeframeQRs = Object.values(
				_.groupBy(datedMarkedQRs, (dms) =>
					[DateTime.fromISO(dms.dateAndTime).toLocaleString(), dms.annotationClassifier, dms.modifiedBy].join(),
				),
			);

			for (const mts of markedTimeframeQRs) {
				const markedPlotSubsamples = mts;

				if (markedPlotSubsamples.length > 0) {
					const excludedPlotSubsamples = _.filter(markedPlotSubsamples, {
						quantifiedRegionStatusName: "Excluded",
					});
					const flaggedPlotSubsamples = _.filter(markedPlotSubsamples, {
						quantifiedRegionStatusName: "Flagged",
					});

					const groupedExcludedPlotSubsamples = _.groupBy(excludedPlotSubsamples, "quantifiedRegionTypeId");
					const groupedFlaggedPlotSubsamples = _.groupBy(flaggedPlotSubsamples, "quantifiedRegionTypeId");

					const checkIfAll = (splitMarkedSubsamples: _.Dictionary<MarkedQuantifiedRegion[]>) => {
						return _.some(Object.keys(splitMarkedSubsamples), (sms) => {
							const plotOutlineCount = _.find(plotsData.quantifiedRegionOutlines, {
								quantifiedRegionTypeId: sms,
							})?.count;
							if (!plotOutlineCount) return false;
							const totalMarkedCount = _.sumBy(Object.values(splitMarkedSubsamples), (g) => g.length);
							return totalMarkedCount >= plotOutlineCount;
						});
					};

					const timeframeStatus: {
						status: "Excluded" | "Flagged" | "PartiallyExcluded" | "PartiallyFlagged";
						statusIcon: "warn-filled" | "warn-line" | "flag-filled" | "flag-line";
						level: "Partial" | "Entire Plot";
						notes: string;
						quantifiedRegionNames: { [key: string]: string[] };
						statusOrder: number;
						levelOrder: 1 | 0;
						isAllData: boolean;
						annotationClassifier: string;
						modifiedBy: string;
						modifiedDateTime: string;
					}[] = [];

					const learnStatus = (
						splitMarkedSubsamples: _.Dictionary<MarkedQuantifiedRegion[]>,
						statusName: "Excluded" | "Flagged",
						statusOrder: number,
					) => {
						if (Object.keys(splitMarkedSubsamples).length > 0) {
							const subsampleTypeNumbers: { [id: string]: number[] } = {};
							let notes = "";

							const qrstids = Object.keys(splitMarkedSubsamples);
							for (const id of qrstids) {
								const subsampleNumbers = _.map(splitMarkedSubsamples[id], "quantifiedRegionNumber") as number[];
								subsampleTypeNumbers[id] = subsampleNumbers.sort((a, b) => a - b);

								const notesSet = _.uniq(_.map(splitMarkedSubsamples[id], "notes"));

								notes += _.reduce(notesSet, (acc, n) => (n ? acc + n + ", " : ""), "");
							}

							// taken from https://stackoverflow.com/questions/2270910/how-to-reduce-consecutive-integers-in-an-array-to-hyphenated-range-expressions
							const getRanges = (array: number[]) => {
								const ranges: string[] = [];
								let rstart: number, rend: number;
								for (let i = 0; i < array.length; i++) {
									rstart = array[i];
									rend = rstart;
									while (array[i + 1] - array[i] === 1) {
										rend = array[i + 1]; // increment the index if the numbers sequential
										i++;
									}
									ranges.push(rstart === rend ? "S" + rstart + "" : "S" + rstart + "–S" + rend);
								}
								return ranges;
							};

							const subsampleTypeNames: { [key: string]: string[] } = {};

							for (const key of Object.keys(subsampleTypeNumbers)) {
								subsampleTypeNames[key] = getRanges(subsampleTypeNumbers[key]);
							}

							notes = notes.slice(0, notes.length - 2);

							let level: "Partial" | "Entire Plot" = "Partial";

							if (checkIfAll(splitMarkedSubsamples)) {
								level = "Entire Plot";
							}

							const finalStatus = level === "Partial" ? (`Partially${statusName}` as const) : statusName;

							timeframeStatus.push({
								status: finalStatus,
								statusIcon: statusIconMap[finalStatus] as "warn-filled" | "warn-line" | "flag-filled" | "flag-line",
								level: level,
								notes: notes,
								quantifiedRegionNames: subsampleTypeNames,
								statusOrder: statusOrder,
								levelOrder: level === "Partial" ? 1 : 0,
								isAllData: markedPlotSubsamples[0]?.isAllData,
								annotationClassifier: markedPlotSubsamples[0]?.annotationClassifier,
								modifiedBy: markedPlotSubsamples[0]?.modifiedBy,
								modifiedDateTime: markedPlotSubsamples[0]?.modifiedDateTime,
							});
						}
					};

					learnStatus(groupedExcludedPlotSubsamples, "Excluded", 0);
					learnStatus(groupedFlaggedPlotSubsamples, "Flagged", 1);

					const dateAndTime = DateTime.fromISO(markedPlotSubsamples[0].dateAndTime).toMillis();
					if (learnedStatusMap[plotId]?.[dateAndTime]) {
						learnedStatusMap[plotId][dateAndTime].push(...timeframeStatus);
					} else if (learnedStatusMap[plotId]) {
						learnedStatusMap[plotId][dateAndTime] = timeframeStatus;
					} else {
						learnedStatusMap[plotId] = { [dateAndTime]: timeframeStatus };
					}
				}
			}
		});

		return learnedStatusMap;
	}, [plotsData, statusIconMap]);

	// Step 2: Build base features and lower lefts with coordinate data (data that comes in from "plotData")
	const { basePlotFeatures, plotLLFeatures, averageBearing, computedTrialPolygon } = useMemo(() => {
		if (plotsLoading || !plotsData || !plotStatusMap || !MFAViewer)
			return { basePlotFeatures: null, plotLLFeatures: null, averageBearing: null, computedTrialPolygon: null };
		const buildProperties = (p: PlotProps) => {
			const status: PlotStatus | object = plotStatusMap[p.plotId]
				? _.flattenDeep(Object.values(plotStatusMap[p.plotId]))[0]
				: {};
			if (status) {
				const plotProps: PlotProps = {
					...p,
					...status,
				};
				return plotProps;
			}
			return {
				...p,
				status,
			};
		};

		const plotPolygons: GeoJSON.Feature<Polygon, PlotProps>[] = [];
		const plotLLs: GeoJSON.Feature<Point, PlotLLProps>[] = [];
		const bearings: number[] = [];

		for (let i = 0; i < plotsData.plots.length; i++) {
			const p = plotsData.plots[i];
			const poly = turf.polygon(
				[
					[
						[p.llLong, p.llLat],
						[p.lrLong, p.lrLat],
						[p.urLong, p.urLat],
						[p.ulLong, p.ulLat],
						[p.llLong, p.llLat],
					],
				],
				buildProperties(p),
			);

			// for getting bearings of plot
			const bottomLine = turf.lineString([
				[p.llLong, p.llLat],
				[p.lrLong, p.lrLat],
			]);
			const leftLine = turf.lineString([
				[p.llLong, p.llLat],
				[p.ulLong, p.ulLat],
			]);

			const lowerLeft = poly.geometry.coordinates[0][0];
			const upperLeft = poly.geometry.coordinates[0][1];
			let squareUpperRight: GeoJSON.Feature<GeoJSON.Point>;
			if (turf.length(bottomLine) <= turf.length(leftLine)) {
				const longBearing = turf.bearing(leftLine.geometry.coordinates[0], leftLine.geometry.coordinates[1]);
				squareUpperRight = turf.transformTranslate(
					turf.point(poly.geometry.coordinates[0][1]),
					turf.length(bottomLine),
					longBearing,
				);
			} else {
				const longBearing = turf.bearing(bottomLine.geometry.coordinates[0], bottomLine.geometry.coordinates[1]);
				squareUpperRight = turf.transformTranslate(
					turf.point(poly.geometry.coordinates[0][3]),
					turf.length(leftLine),
					longBearing,
				);
			}

			bearings.push(turf.bearing(lowerLeft, upperLeft));

			const bearing = turf.bearing(lowerLeft, squareUpperRight);
			const iconScaleFactor = Math.min(p.plotColumnWidth, p.plotRangeLength) / 7;
			// const iconShiftFactor = 3;

			const ll = turf.point([p.llLong, p.llLat], {
				...poly.properties,
				bearing,
				iconScaleFactor,
			});

			const ll_shifted = turf.transformTranslate(ll, ((iconScaleFactor * 3.15) / 2) * Math.SQRT2, bearing, {
				units: "feet",
			});

			plotPolygons.push(poly);
			plotLLs.push(ll_shifted);
		}

		const polysCollection = turf.featureCollection(plotPolygons);
		const llsCollection = turf.featureCollection(plotLLs);
		const averageBearing = _.mean(bearings);

		// from https://github.com/matthiasfeist/geojson-minimum-bounding-rectangle
		const trialHull = turf.convex(polysCollection);
		let computedTrialPolygon: GeoJSON.Polygon | null = null;
		if (trialHull) {
			const centroidCoords = turf.centroid(trialHull);
			const allHullCoords = turf.coordAll(trialHull);

			let minArea = Number.MAX_SAFE_INTEGER;
			let trialPolygon: GeoJSON.Feature<GeoJSON.Polygon> | null = null;

			for (let index = 0; index < allHullCoords.length - 1; index++) {
				const angle = turf.bearing(allHullCoords[index], allHullCoords[index + 1]);

				const rotatedHull = turf.transformRotate(trialHull, -1.0 * angle, {
					pivot: centroidCoords,
				});

				const envelopeOfHull = turf.envelope(rotatedHull);
				const envelopeArea = turf.area(envelopeOfHull);
				if (envelopeArea < minArea) {
					minArea = envelopeArea;
					trialPolygon = turf.transformRotate(envelopeOfHull, angle, {
						pivot: centroidCoords,
					});
				}
			}

			if (trialPolygon) {
				// setTrialBox(trialPolygon);
				computedTrialPolygon = trialPolygon.geometry;
			}
		}
		return { basePlotFeatures: polysCollection, plotLLFeatures: llsCollection, averageBearing, computedTrialPolygon };
	}, [plotsLoading, plotsData, plotStatusMap, MFAViewer]);

	// Publish the derived trial polygon to context in an effect; dispatching from
	// the memo above would update DataProvider while this component is rendering.
	useEffect(() => {
		if (computedTrialPolygon) {
			updateTrialPolygon(computedTrialPolygon);
		}
	}, [computedTrialPolygon, updateTrialPolygon]);

	// Post Step 2: Zoom the map to the plots
	useEffect(() => {
		if (!MFAViewer || !trialPolygon || averageBearing === null) {
			return () => {};
		}
		const jumpToBounds = () => {
			MFAViewer.jumpTo({
				bearing: averageBearing - 90,
				center: trialPolygon.coordinates[0][0] as [number, number],
				zoom: 17,
				pitch: 0,
			});

			MFAViewer.fitBounds(
				[trialPolygon.coordinates[0][0] as [number, number], trialPolygon.coordinates[0][2] as [number, number]],
				{
					bearing: averageBearing - 90,
				},
			);
		};

		MFAViewer.on("load", jumpToBounds);
		// incase the map is already loaded
		jumpToBounds();

		return () => {
			MFAViewer.off("load", jumpToBounds);
		};
	}, [MFAViewer, trialPolygon, averageBearing]);

	// Step 3a: Add treatments and kick off call to build treatment polygons (should be an earlier step as it is expensive!)
	const plotFeaturesWithTreatments = useMemo(() => {
		if (!basePlotFeatures?.features?.length) return null;
		if (!experimentalDesign || !treatmentNames) return basePlotFeatures;
		const newFeatures = turf.featureReduce(
			basePlotFeatures,
			(acc, curr) => {
				const exp = experimentalDesign.find((d) => d.plotId === curr.properties.plotId);
				curr.properties.superPlot = exp?.plotGroup;
				curr.properties.displayName = exp?.plotGroup
					? exp?.treatment
						? exp.plotGroup.toString() + "\n" + exp.treatment
						: exp.plotGroup.toString()
					: "";
				curr.properties.treatment = exp?.treatment ?? 0;
				curr.properties.treatmentName = exp?.treatment !== undefined ? treatmentNames[exp.treatment] : "";
				acc.push(curr);
				return acc;
			},
			[] as Feature<Polygon, PlotProps>[],
		);
		const newCollection = turf.featureCollection(newFeatures);

		const firstFeatProps = basePlotFeatures.features[0].properties;
		const tolerance = turf.convertLength(
			Math.max(firstFeatProps.plotColumnWidth, firstFeatProps.plotRangeLength) * 0.25,
			"feet",
			"meters",
		);

		const cleanedFeatures: FeatureCollection<Polygon, { treatment: number }> = turf.featureCollection(
			turf.featureReduce(
				newCollection,
				(acc, curr) => {
					const clone = turf.clone(curr) as unknown as Feature<Polygon, { treatment: number }>;
					clone.properties = { treatment: curr.properties.treatment };
					acc.push(clone);
					return acc;
				},
				[] as Feature<Polygon, { treatment: number }>[],
			),
		);

		generateTreatmentPolygonsMutation({
			getAccessToken: getTokenSilently,
			clientId: userAuth.currentClientId!,
			plotFeatures: cleanedFeatures,
			tolerance,
		});

		return newCollection;
	}, [
		basePlotFeatures,
		experimentalDesign,
		treatmentNames,
		generateTreatmentPolygonsMutation,
		getTokenSilently,
		userAuth.currentClientId,
	]);

	// Step 3b: Modify response from generated treatment polygons (no other steps depend on this)
	const treatmentPolygons = useMemo(() => {
		if (!generatedTreatmentPolygonsResponse || isGeneratingTreatmentPolygons) return null;
		const polygons: FeatureCollection<Polygon | MultiPolygon, { treatment: number; color?: string; name?: string }> =
			generatedTreatmentPolygonsResponse.data;
		turf.featureEach(polygons, (f) => {
			f.properties.color = styles.getColorFromPalette(f.properties.treatment - 1);
			f.properties.name = treatmentNames[f.properties.treatment];
		});
		return polygons;
	}, [generatedTreatmentPolygonsResponse, treatmentNames, isGeneratingTreatmentPolygons]);

	// Step 4: Add heatmap data
	const plotFeaturesWithHeatmap = useMemo(() => {
		if (!plotFeaturesWithTreatments) return null;
		if (!heatmapData) return plotFeaturesWithTreatments;

		const dataMap = _.reduce(
			heatmapData,
			(acc, hd) => {
				return {
					...acc,
					[hd.plotId]: { value: hd.plotAnalysisResultValue, valueLabel: hd.plotAnalysisResultValue?.toFixed(3) },
				};
			},
			{} as { [plotId: string]: { value?: number; valueLabel?: string } },
		);

		const newFeatures = turf.featureReduce(
			plotFeaturesWithTreatments,
			(acc, curr) => {
				curr.properties = { ...curr.properties, ...dataMap[curr.properties.plotId] };
				acc.push(curr);
				return acc;
			},
			[] as Feature<Polygon, PlotProps>[],
		);
		return turf.featureCollection(newFeatures);
	}, [plotFeaturesWithTreatments, heatmapData]);

	// Step 5: Add group assignments and finish up plot features
	const plotFeatures = useMemo(() => {
		if (!plotFeaturesWithHeatmap) return null;
		if (!dataLayerGroupMap) return plotFeaturesWithHeatmap;

		const newFeatures = turf.featureReduce(
			plotFeaturesWithHeatmap,
			(acc, curr) => {
				if (dataLayerGroupMap) {
					curr.properties.group = dataLayerGroupMap[curr.properties.plotId.toUpperCase()] ?? 0;
				} else {
					curr.properties.group = 0; // unassigned
				}
				acc.push(curr);
				return acc;
			},
			[] as Feature<Polygon, PlotProps>[],
		);

		return turf.featureCollection(newFeatures);
	}, [dataLayerGroupMap, plotFeaturesWithHeatmap]);

	// Step 6: Wrap up plotData for context and build centers
	const plotCenterFeatures = useMemo(() => {
		if (!plotFeatures) return null;
		const centerFeatures = turf.featureReduce(
			plotFeatures,
			(acc, curr) => {
				const center = turf.centroid<PlotProps>(curr);
				center.properties = curr.properties;
				acc.push(center);
				return acc;
			},
			[] as GeoJSON.Feature<GeoJSON.Point, PlotProps>[],
		);

		return turf.featureCollection(centerFeatures);
	}, [plotFeatures]);

	// Same as above: push plot props to context from an effect, not during render.
	useEffect(() => {
		if (!plotFeatures) return;
		updatePlotData(turf.propReduce(plotFeatures, (acc, curr) => acc.concat(curr), [] as PlotProps[]));
	}, [plotFeatures, updatePlotData]);

	const mapboxTileSourcesLayers: { source: SourceProps; layer: LayerProps }[] | null = useMemo(() => {
		if (viewerMapboxUploads.length > 0) {
			const sourcesLayers = _.map(viewerMapboxUploads, (vmu) => {
				const sourceProps: SourceProps = {
					id: mapboxTileSourceBaseId + vmu.tilesetId,
					type: "raster",
					url: `mapbox://${vmu.tilesetId}`,
					// tileSize: 256
				};

				const layerProps: LayerProps = {
					id: mapboxTileLayerBaseId + vmu.tilesetId,
					type: "raster",
					source: mapboxTileSourceBaseId + vmu.tilesetId,
					paint: {
						"raster-opacity": orthoVisible ? 1 : 0,
					},
				};
				return { source: sourceProps, layer: layerProps };
			});
			return sourcesLayers;
		}
		return null;
	}, [viewerMapboxUploads, orthoVisible]);

	useEffect(() => {
		if (dataLayerGeometry?.properties.attributeDescriptions) {
			updateDataLayerNames(dataLayerGeometry.properties.attributeDescriptions);
			updateAxis({ xOrder: dataLayerGeometry.properties.attributeDescriptions });
			// Build values array with same 1-based indexing as attributeDescriptions:
			// values[aerialfarmsStd] = catValueNum/catValueText/aerialfarmsStd for that zone.
			const values: (number | string)[] = [];
			_.chain(dataLayerGeometry.features)
				.uniqBy((f) => f.properties.aerialfarmsStd)
				.forEach((f) => {
					values[f.properties.aerialfarmsStd] =
						f.properties.catValueNum ?? f.properties.catValueText ?? f.properties.aerialfarmsStd;
				})
				.value();
			updateDataLayerValues(values);
		}
	}, [dataLayerGeometry, updateAxis, updateDataLayerNames, updateDataLayerValues]);

	const offsetPlotAssociatedGeometry = useMemo(() => {
		if (plotFeatures && plotAssociatedGeometry) {
			const from = turf.center(plotAssociatedGeometry);
			const to = turf.center(plotFeatures);
			const bearing = turf.rhumbBearing(from, to);
			const distance = turf.rhumbDistance(from, to);
			return turf.transformTranslate(plotAssociatedGeometry, distance, bearing);
		}
		return undefined;
	}, [plotAssociatedGeometry, plotFeatures]);

	const offsetTreatmentGeometry = useMemo(() => {
		if (plotFeatures && treatmentPolygons) {
			const from = turf.center(treatmentPolygons);
			const to = turf.center(plotFeatures);
			const bearing = turf.rhumbBearing(from, to);
			const distance = turf.rhumbDistance(from, to);
			return turf.transformTranslate(treatmentPolygons, distance, bearing);
		}
		return undefined;
	}, [treatmentPolygons, plotFeatures]);

	const plotPolysSource: SourceProps = useMemo(
		() => ({
			id: _.uniqueId("mfa-plot-polys-"),
			type: "geojson",
			data: plotFeatures ?? undefined,
		}),
		[plotFeatures],
	);

	const plotCentersSource: SourceProps = useMemo(
		() => ({
			id: _.uniqueId("mfa-plot-centers-"),
			type: "geojson",
			data: plotCenterFeatures ?? undefined,
		}),
		[plotCenterFeatures],
	);

	const plotLLsSource: SourceProps = useMemo(
		() => ({
			id: _.uniqueId("mfa-plot-lls-"),
			type: "geojson",
			data: plotLLFeatures ?? undefined,
		}),
		[plotLLFeatures],
	);

	const zoneSource: SourceProps = useMemo(
		() => ({
			id: "soil-map",
			type: "geojson",
			data: dataLayerGeometryWithColors,
		}),
		[dataLayerGeometryWithColors],
	);

	const plotUnionSource: SourceProps = useMemo(
		() => ({
			id: "plot-layers",
			type: "geojson",
			data: offsetPlotAssociatedGeometry,
		}),
		[offsetPlotAssociatedGeometry],
	);

	const plotTreatmentsSource: SourceProps = useMemo(
		() => ({
			id: "plot-treatments",
			type: "geojson",
			data: offsetTreatmentGeometry,
		}),
		[offsetTreatmentGeometry],
		// []
	);

	// const superPlotsSource: SourceProps = useMemo(
	// 	() => ({
	// 		id: "super-plots",
	// 		type: "geojson",
	// 		data: superPlotUnions ?? undefined
	// 		// data: trialBox ?? undefined
	// 	}),
	// 	[superPlotUnions]
	// );

	const heatmapLayer: LayerProps = styles.generateHeatmapLayer(
		heatmapColorStops,
		heatmapVisible ? heatmapOpacity : 0,
		hideExclusions,
	);

	const selectedFilter = ["in", "plotId", ...selectedPlotIds];
	const dataLayerHoverFilter: FilterSpecification = ["==", "aerialfarmsStd", hoveredDataLayer ?? -1];

	const selectPlotIds = useCallback(
		(plotIds: string[]) => {
			const features = _.filter(plotFeatures?.features, (f) => plotIds.includes(f?.properties.plotId));
			updateSelectedFeatures(features);
		},
		[plotFeatures, updateSelectedFeatures],
	);

	const onClick = useCallback(
		(e: mapboxgl.MapMouseEvent) => {
			const features = e.target.queryRenderedFeatures(e.point);

			updateSelectedPlotIds(_.map(features, "properties.plotId") as string[]);
		},
		[updateSelectedPlotIds],
	);

	const onHover = useCallback(
		(e: mapboxgl.MapMouseEvent) => {
			const map = e.target;

			const features = map.queryRenderedFeatures(e.point);
			const annotation = features.find((f) => f.layer?.id === "annotation-icons") as
				| GeoJSON.Feature<GeoJSON.Point, PlotLLProps>
				| undefined;
			if (annotation) {
				setPopupCoords(annotation.geometry.coordinates);
				setPopupOffsetScaleFactor(annotation.properties.iconScaleFactor);
				setFocusedAnnotation(annotation);
				setPopupVisible(true);
				setCursor("pointer");
			} else {
				setFocusedAnnotation(null);
				setPopupVisible(false);
				setCursor("auto");
			}

			// HACK: way to ensure the top-most feature is a data layer
			const dataLayerGroup = [features[0]].find((f) => !!f?.properties?.aerialfarmsStd) as
				| Feature<Polygon | MultiPolygon, { aerialfarmsStd: number }>
				| undefined;
			if (dataLayerGroup) {
				setHoveredDataLayer(dataLayerGroup.properties.aerialfarmsStd);
				if (plotFeatures) {
					updateSelectedGroup(dataLayerGroup.properties.aerialfarmsStd);
				}
			} else {
				setHoveredDataLayer(null);
				updateSelectedGroup(null);
			}
		},
		[plotFeatures, updateSelectedGroup],
	);

	const mapRefCallback = useCallback(
		(ref: MapRef | null) => {
			if (ref !== null) {
				const map = ref;

				const loadIcons = () => {
					_.forOwn(iconSvgs, (svg, name) => {
						if (!map.hasImage(name)) {
							const image = new Image(ICON_SIZE, ICON_SIZE);
							image.crossOrigin = "Anonymous";
							image.onload = () => {
								map.addImage(name, image, { sdf: true });
							};
							image.src = svg;
						}
					});
				};

				loadIcons();
				map.on("styleimagemissing", (e) => {
					const id = e.id; // id of the missing image
					console.warn("Icon failed to load:", id);
					loadIcons();
				});
			}
		},
		[iconSvgs],
	);

	useEffect(() => {
		selectPlotIds(selectedPlotIds);
	}, [selectPlotIds, selectedPlotIds]);

	// useEffect(() => {
	// 	if (selectedGroup && zoneUnionedPlotsMap) {
	// 		setPlotUnion(zoneUnionedPlotsMap[selectedGroup]);
	// 	} else {
	// 		setPlotUnion(null);
	// 	}
	// }, [selectedGroup, zoneUnionedPlotsMap]);

	return (
		<Map
			ref={mapRefCallback}
			style={{ height: 0, flexGrow: 1 }}
			id="MFAViewer"
			projection="mercator"
			initialViewState={INIT_VIEW_STATE}
			cursor={cursor}
			bearingSnap={0}
			// onDragStart={() => setCursor("grabbing")}
			// onDragEnd={() => setCursor("auto")}
			onMouseMove={(e) => {
				onHover(e);
			}}
			onMouseOut={() => {
				setHoveredDataLayer(null);
				updateSelectedGroup(null);
			}}
			onZoom={(e) => {
				setPopupOffset(Math.pow(28 * popupOffsetScaleFactor, (e.viewState.zoom - 14) / (22 - 14)));
			}}
			onClick={(e) => {
				onClick(e);
			}}
			onLoad={() => {
				setMapLoaded(true);
			}}
			mapStyle="mapbox://styles/mapbox/standard-satellite"
			mapboxAccessToken={process.env.MAPBOX_ACCESS_TOKEN}
			doubleClickZoom={false}
		>
			{mapLoaded && (
				<>
					<NavigationControl showZoom={false} />
					<GeolocateControl
						position="top-right"
						// Spacing below the compass so the compass/geolocate/toolbox column
						// reads as evenly stacked. Applied to the control's own container.
						style={{ marginTop: 42, marginRight: 2 }}
						positionOptions={{ enableHighAccuracy: true }}
						trackUserLocation={true}
						showUserHeading={true}
						showAccuracyCircle={false}
						// The control's built-in fitBounds would reset zoom/orientation to frame
						// the accuracy circle. A high maxZoom stops it from zooming way out; the
						// onGeolocate handler below then restores the camera we snapshotted at
						// tracking start, preserving the user's trial framing (zoom/bearing/pitch).
						fitBoundsOptions={{ maxZoom: 24 }}
						onTrackUserLocationStart={() => {
							if (!MFAViewer) return;
							// Snapshot the camera so the first fly-to keeps this framing. Consumed
							// once in onGeolocate so later GPS updates (or manual zooming while
							// tracking) don't keep snapping the zoom back to this value.
							preGeolocateCamera.current = {
								zoom: MFAViewer.getZoom(),
								bearing: MFAViewer.getBearing(),
								pitch: MFAViewer.getPitch(),
							};
						}}
						onGeolocate={(e) => {
							const camera = preGeolocateCamera.current;
							if (!MFAViewer || !camera) return;
							// GeolocationPosition.coords is a prototype getter, so it can be lost
							// when mapbox merges the position onto the forwarded event (e.coords
							// undefined). Fall back to the control's raw _lastKnownPosition.
							const coords =
								e.coords ??
								(e.target as unknown as { _lastKnownPosition?: GeolocationPosition })._lastKnownPosition?.coords;
							if (!coords) return;
							preGeolocateCamera.current = null;
							MFAViewer.easeTo({
								center: [coords.longitude, coords.latitude],
								...camera,
							});
						}}
					/>
					{/* Draw modes and layers, we hide controls and use the ref to control drawing */}
					<DrawControl
						ref={drawRef}
						modes={{ ...MapboxDraw.modes, ...MeasureModes }}
						displayControlsDefault={false}
						onCreate={({ features }) => {
							updateDrawnFeature(features[0]);
						}}
						onUpdate={({ features }) => {
							updateDrawnFeature(features[0]);
						}}
						onDelete={() => {
							updateDrawnFeature(null);
						}}
					/>

					{/* static "Z-Layers" for correct ordering of dynamic layers
					    0.5 layers for elements that lay ontop of their base layer (i.e.: plot labels) */}
					<Source type="geojson" data={turf.featureCollection([])}>
						<Layer id="z-2.5" type="symbol" />
						<Layer id="z-2" type="symbol" beforeId="z-2.5" />
						<Layer id="z-1.5" type="symbol" beforeId="z-2" />
						<Layer id="z-1" type="symbol" beforeId="z-1.5" />
						<Layer id="z-0.5" type="symbol" beforeId="z-1" />
						<Layer id="z-0" type="symbol" beforeId="z-0.5" />
					</Source>

					{/* render treatment zones */}
					{plotTreatmentsSource && treatmentsVisible && !!treatmentPolygons && (
						<Source key={plotTreatmentsSource.id} {...plotTreatmentsSource}>
							<Layer
								id="plot-treatment-lines"
								type="line"
								paint={{
									"line-color": ["get", "color"],
									"line-width": 2,
								}}
								filter={[
									"!in",
									"name",
									...Object.entries(legendStatus).reduce(
										// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
										(acc, curr) => (curr[1] === "legendonly" ? [...acc, curr[0]] : acc),
										[],
									),
								]}
								beforeId={`z-${2.5 - layerOrder.indexOf("heatmap")}`}
							/>
						</Source>
					)}

					{/* render super plots */}
					{/* {superPlotsSource && (
						<Source key={superPlotsSource.id} {...superPlotsSource}>
							<Layer
								id="super-plot-lines"
								type="line"
								paint={{
									"line-color": "#238eff",
									"line-width": 1
								}}
								beforeId={`z-${2.5 - layerOrder.indexOf("heatmap")}`}
							/>
						</Source>
					)} */}

					{/* render hovered zones plots */}
					{plotUnionSource.data && (
						<Source key={plotUnionSource.id} {...plotUnionSource}>
							<Layer
								id="plot-union-lines-hovered"
								type="line"
								paint={{
									"line-color": "#ff3300",
									"line-width": 2,
								}}
								beforeId={`z-${2.5 - layerOrder.indexOf("heatmap")}`}
								filter={dataLayerHoverFilter}
							/>
						</Source>
					)}

					{/* render plots */}
					{plotFeatures && !plotsLoading && plotsVisible && (
						<Source key={plotPolysSource.id} {...plotPolysSource}>
							<Layer {...styles.outlinePlotLayer} beforeId={`z-${2 - layerOrder.indexOf("heatmap")}`} />
							<Layer {...styles.plotLayer} beforeId={`z-${2 - layerOrder.indexOf("heatmap")}`} />
							<Layer {...heatmapLayer} beforeId={`z-${2 - layerOrder.indexOf("heatmap")}`} />
							<Layer
								{...styles.hoveredPlotLayer}
								filter={selectedFilter}
								beforeId={`z-${2 - layerOrder.indexOf("heatmap")}`}
							/>
						</Source>
					)}

					{/* render labels */}
					{plotCenterFeatures && plotsVisible && plotLabelsVisible !== "none" && (
						<Source key={plotCentersSource.id} {...plotCentersSource}>
							<Layer
								{...styles.generatePlotLabelLayer(plotLabelsVisible === "heatmap" ? "valueLabel" : "displayName")}
								beforeId={`z-${2.5 - layerOrder.indexOf("heatmap")}`}
							/>
						</Source>
					)}

					{/* render annotations */}
					{plotLLFeatures && plotsVisible && statusMarkersVisible && (
						<Source key={plotLLsSource.id} {...plotLLsSource}>
							<Layer {...styles.annotationLayer} beforeId={`z-${2.5 - layerOrder.indexOf("heatmap")}`} />
						</Source>
					)}

					{/* render data layer */}
					{dataLayerVisible && (
						<Source key={zoneSource.id} {...zoneSource}>
							<Layer {...styles.generateZoneLayer(dataLayerOpacity)} beforeId={`z-${2 - layerOrder.indexOf("data")}`} />
							<Layer
								{...styles.generateZonePatternLayer(dataLayerOpacity)}
								beforeId={`z-${2 - layerOrder.indexOf("data")}`}
							/>
							<Layer
								id="zone-lines"
								type="line"
								paint={{
									"line-color": "#ffff00",
									"line-width": 2,
								}}
								beforeId={`z-${2 - layerOrder.indexOf("data")}`}
							/>
							<Layer
								id="zone-fill-hovered"
								type="fill"
								paint={{
									"fill-color": "#00ff44",
									"fill-opacity": 0.35,
								}}
								filter={dataLayerHoverFilter}
								beforeId={`z-${2 - layerOrder.indexOf("data")}`}
							/>
							<Layer
								id="zone-lines-hovered"
								type="line"
								paint={{
									"line-color": "#00ff44",
									"line-width": 2,
								}}
								filter={dataLayerHoverFilter}
								beforeId={`z-${2 - layerOrder.indexOf("data")}`}
							/>
						</Source>
					)}

					{/* render deck.gl overlay */}
					<DeckGLOverlay layers={deckLayers} interleaved />

					{/* render mapbox tile rasters */}
					{mapboxTileSourcesLayers &&
						deckLayers.length === 0 &&
						mapboxTileSourcesLayers.map((mtsl) => {
							return (
								<Source key={mtsl.source.id} {...mtsl.source}>
									<Layer key={mtsl.layer.id} beforeId={`z-${2 - layerOrder.indexOf("ortho")}`} {...mtsl.layer} />
								</Source>
							);
						})}

					{popupVisible && plotStatusMap && focusedAnnotation && (
						<Popup
							onClose={() => setPopupVisible(false)}
							anchor="left"
							offset={popupOffset}
							longitude={popupCoords[0]}
							latitude={popupCoords[1]}
							maxWidth="400px"
							closeButton={false}
						>
							<div>
								<h3 style={{ marginBottom: "8px", fontSize: "20px" }}>
									<u>Annotation Summary for Plot {focusedAnnotation.properties.plotName}</u>
								</h3>
								{_.reduce(
									plotStatusMap[focusedAnnotation.properties.plotId],
									(acc, statuses, date) => {
										acc.push(
											<div
												key={DateTime.fromMillis(Number(date)).toLocal().toLocaleString()}
												style={{ fontSize: "16px" }}
											>
												<p style={{ marginBottom: "4px" }}>
													<u>
														{!statuses[0].isAllData
															? DateTime.fromMillis(Number(date)).toLocal().toLocaleString()
															: "All Dates"}
														:
													</u>
												</p>
												{_.map(statuses, (status) => (
													<p key={status.annotationClassifier + status.level}>
														<b>{status.status}:</b> {status.annotationClassifier} - {status.notes} - {status.modifiedBy}{" "}
														on {DateTime.fromISO(status.modifiedDateTime).toLocal().toLocaleString()}
													</p>
												))}
											</div>,
										);
										return acc;
									},
									[] as JSX.Element[],
								)}
							</div>
						</Popup>
					)}
				</>
			)}
		</Map>
	);
};

export default MFAMapWrapper;
